import { useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import * as scanner from '../../platform/scanner';
import {
  getPortState,
  reconnectSerial,
  subscribePortState,
  useScanTarget,
} from '../../platform/scanning';
import {
  BAUD_RATES,
  forgetPorts,
  requestPort,
  serialSupport,
  type SerialSupport,
} from '../../platform/serial';
import {
  SCANNER_MODES,
  getScannerSettings,
  setScannerSettings,
  subscribeScannerSettings,
  type ScannerMode,
} from '../../platform/scannerSettings';

/**
 * What this workstation reads codes with.
 *
 * Device settings, not account settings: the same person's phone and desk PC
 * need different answers, so nothing here is sent to the server.
 *
 * The test field is the point of the screen. "Is the scanner working" is a
 * question people otherwise answer by trying to receive a parcel with it, and
 * finding out there that it is not.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const settings = useSyncExternalStore(subscribeScannerSettings, getScannerSettings);

  const [last, setLast] = useState<string | null>(null);
  const port = useSyncExternalStore(subscribePortState, getPortState, () => 'idle');

  // While this screen is open every reading lands here rather than opening a
  // record — which is exactly what someone testing a scanner wants.
  useScanTarget(setLast);

  const serial: SerialSupport = serialSupport();
  const camera = scanner.cameraSupport();

  const connect = async () => {
    const granted = await requestPort();
    if (!granted) return;
    setScannerSettings({ mode: 'serial' });
    // The mode was already "serial" — the person set it before pressing this —
    // so nothing the provider watches has changed and it would not open the
    // port that was just granted. This is what says "now try again".
    reconnectSerial();
  };

  const disconnect = async () => {
    await forgetPorts();
    setScannerSettings({ mode: 'keyboard' });
    reconnectSerial();
  };

  return (
    <div className="settings">
      <h2>{t('settings.title')}</h2>

      <section className="card">
        <h3>{t('settings.scanner.title')}</h3>
        <p className="hint">{t('settings.scanner.intro')}</p>

        <div className="settings__row">
          <label htmlFor="scanner-mode">{t('settings.scanner.mode')}</label>
          <select
            id="scanner-mode"
            value={settings.mode}
            onChange={(event) => setScannerSettings({ mode: event.target.value as ScannerMode })}
          >
            {SCANNER_MODES.map((mode) => (
              <option key={mode} value={mode} disabled={mode === 'serial' && serial !== 'ok'}>
                {t(`settings.scanner.modes.${mode}`)}
              </option>
            ))}
          </select>
        </div>

        {settings.mode === 'keyboard' && <p className="hint">{t('settings.scanner.keyboardHint')}</p>}

        {settings.mode === 'serial' && (
          <>
            <div className="settings__row">
              <label htmlFor="scanner-baud">{t('settings.scanner.baud')}</label>
              <select
                id="scanner-baud"
                value={settings.baudRate}
                onChange={(event) => setScannerSettings({ baudRate: Number(event.target.value) })}
              >
                {BAUD_RATES.map((rate) => (
                  <option key={rate} value={rate}>
                    {rate}
                  </option>
                ))}
              </select>
            </div>
            <div className="settings__row">
              <button type="button" className="btn" onClick={() => void connect()}>
                {t('settings.scanner.choosePort')}
              </button>
              <button type="button" className="btn" onClick={() => void disconnect()}>
                {t('settings.scanner.forgetPort')}
              </button>
            </div>
            {/* One line about the port, not two. It said "chosen" and "not
                chosen" at the same time: one message reported what the button
                had done, the other what the port was actually doing, and only
                the second one was true. */}
            <p className={port === 'open' ? 'stock__ok' : 'panel__error'}>
              {port === 'open' && t('settings.scanner.state.open')}
              {port === 'no-port' && t('settings.scanner.state.noPort')}
              {port === 'idle' && t('settings.scanner.state.idle')}
              {port !== 'open' && port !== 'no-port' && port !== 'idle' && (
                t('settings.scanner.state.failed', { reason: port })
              )}
            </p>
            <p className="hint">{t('settings.scanner.terminator')}</p>
          </>
        )}

        {serial !== 'ok' && <p className="hint">{t(`settings.scanner.serial.${serial}`)}</p>}

        <div className="settings__test">
          <h4>{t('settings.scanner.test')}</h4>
          <p className="hint">{t('settings.scanner.testHint')}</p>
          <output className="settings__reading">
            {last ?? <span className="hint">{t('settings.scanner.nothingYet')}</span>}
          </output>
        </div>
      </section>

      <section className="card">
        <h3>{t('settings.camera.title')}</h3>
        <p className="hint">
          {camera === 'ok' ? t('settings.camera.ready') : t(`scan.camera.${camera}`)}
        </p>
      </section>
    </div>
  );
}
